import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { toolsApi } from '@/api/tools';
import { integrationsApi } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import type {
  IntegrationStatus,
  PersistedToolRecord,
  ToolCatalogItem,
  ToolRecord,
} from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { cn, formatDate } from '@/lib/utils';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { ToolWizard } from '@/components/tools/wizard/ToolWizard';

const UNGROUPED = '__none__';

interface IntegrationView {
  status: IntegrationStatus | undefined;
  configured: boolean;
  enabled: boolean;
  hasIntegration: boolean;
  label: string;
}

export function Tools(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId } = useWorkspace();
  const { data, isLoading, error } = useQuery({
    queryKey: ['tools'],
    queryFn: toolsApi.list,
    staleTime: 30_000,
  });

  const integrationsQuery = useQuery({
    queryKey: ['integrations', currentBotId],
    queryFn: () => integrationsApi.list(),
    enabled: !!currentBotId,
    staleTime: 30_000,
  });
  const systemReady = !!integrationsQuery.data;
  const statusById = React.useMemo(() => {
    const m = new Map<string, IntegrationStatus>();
    for (const s of integrationsQuery.data?.items ?? []) m.set(s.instanceId, s);
    return m;
  }, [integrationsQuery.data]);

  const [wizardOpen, setWizardOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<ToolRecord | null>(null);
  const [filter, setFilter] = React.useState('');

  const deleteMutation = useMutation({
    mutationFn: (name: string) => toolsApi.delete(name),
    onSuccess: () => {
      toast.success(t('tools.deletedToast'));
      void queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ name, enabled }: { name: string; enabled: boolean }) =>
      toolsApi.setEnabled(name, enabled),
    onSuccess: (res) => {
      toast.success(res.enabled ? t('tools.enabledToast') : t('tools.disabledToast'));
      void queryClient.invalidateQueries({ queryKey: ['tools'] });
    },
    onError: (err) => toast.error(unwrapError(err)),
  });

  const onCreate = (): void => {
    setEditing(null);
    setWizardOpen(true);
  };

  const onEdit = async (name: string): Promise<void> => {
    try {
      const detail = await toolsApi.get(name);
      if (!('kind' in detail) || !detail.kind) {
        toast.error(t('tools.detailUnavailable'));
        return;
      }
      setEditing(detail as PersistedToolRecord);
      setWizardOpen(true);
    } catch (err) {
      toast.error(unwrapError(err));
    }
  };

  const onDelete = (item: ToolCatalogItem): void => {
    if (!window.confirm(t('tools.confirmDelete', { name: item.name }))) return;
    deleteMutation.mutate(item.name);
  };

  const filtered = React.useMemo(() => {
    const items = data ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((tool) => tool.name.includes(q) || tool.description.toLowerCase().includes(q));
  }, [data, filter]);

  const groups = React.useMemo(() => {
    const byGroup = new Map<string, ToolCatalogItem[]>();
    for (const tool of filtered) {
      const key = tool.integrationId ?? tool.integration ?? UNGROUPED;
      const arr = byGroup.get(key) ?? [];
      arr.push(tool);
      byGroup.set(key, arr);
    }
    const ordered: { key: string; tools: ToolCatalogItem[] }[] = [];
    for (const [k, tools] of byGroup) {
      ordered.push({ key: k, tools });
    }
    return ordered;
  }, [filtered]);

  const viewFor = (key: string): IntegrationView => {
    const hasIntegration = key !== UNGROUPED;
    const status = hasIntegration ? statusById.get(key) : undefined;
    return {
      status,
      configured: !!status,
      enabled: !!status?.enabled,
      hasIntegration,
      label: status?.label ?? key,
    };
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('tools.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('tools.subtitle')}</p>
        </div>
        <Button onClick={onCreate} className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t('tools.newCustom')}
        </Button>
      </header>

      <div className="flex items-center gap-2">
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('tools.filterPlaceholder')}
          className="max-w-md"
        />
        <span className="text-xs text-muted-foreground">
          {filtered.length} / {data?.length ?? 0}
        </span>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </CardContent>
        </Card>
      ) : error ? (
        <p className="text-sm text-destructive">{unwrapError(error)}</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm text-muted-foreground">
            {t('tools.empty')}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groups.map(({ key, tools }) => {
            const view = viewFor(key);
            return (
              <section key={key} className="space-y-2">
                <CategoryHeader groupKey={key} view={view} count={tools.length} />
                <div className="grid gap-2">
                  {tools.map((tool) => (
                    <ToolRow
                      key={tool.name}
                      tool={tool}
                      view={view}
                      gate={systemReady}
                      onEdit={() => void onEdit(tool.name)}
                      onDelete={() => onDelete(tool)}
                      onToggle={(enabled) => toggleMutation.mutate({ name: tool.name, enabled })}
                      busy={deleteMutation.isPending}
                      toggling={
                        toggleMutation.isPending && toggleMutation.variables?.name === tool.name
                      }
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <ToolWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        editing={editing}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: ['tools'] });
        }}
      />
    </div>
  );
}

function CategoryHeader({
  view,
  count,
}: {
  groupKey: string;
  view: IntegrationView;
  count: number;
}): JSX.Element {
  const { t } = useTranslation();
  const title = view.hasIntegration ? view.label : t('tools.uncategorized');

  return (
    <div className="flex flex-wrap items-center gap-2 border-b pb-1.5">
      <Settings2 className="h-4 w-4 text-muted-foreground" />
      <h2 className="text-sm font-semibold">{title}</h2>
      {view.status && (
        <Badge variant="outline" className="text-[10px]">{view.status.type}</Badge>
      )}
      <span className="text-xs text-muted-foreground">({count})</span>
      {view.hasIntegration ? (
        view.configured ? (
          view.enabled ? (
            <Badge variant="success" className="gap-1 text-[10px]">
              <CheckCircle2 className="h-3 w-3" />
              {t('tools.integrationActive')}
            </Badge>
          ) : (
            <Badge variant="warning" className="text-[10px]">
              {t('tools.integrationDisabled')}
            </Badge>
          )
        ) : (
          <span className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px]">
              {t('tools.integrationNotConfigured')}
            </Badge>
            <Link to="/integrations" className="text-[11px] text-primary hover:underline">
              {t('tools.configureLink')}
            </Link>
          </span>
        )
      ) : null}
    </div>
  );
}

function ToolRow({
  tool,
  view,
  gate,
  onEdit,
  onDelete,
  onToggle,
  busy,
  toggling,
}: {
  tool: ToolCatalogItem;
  view: IntegrationView;
  gate: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: (enabled: boolean) => void;
  busy: boolean;
  toggling: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(false);
  const isStatic = tool.source === 'static';

  const integrationTitle = view.label;
  const cannotEnable = gate && view.hasIntegration && !view.configured && !tool.enabled;
  const orphanWarning =
    view.hasIntegration && tool.enabled && (!view.configured || !view.enabled)
      ? !view.configured
        ? t('tools.warnUnconfigured', { integration: integrationTitle })
        : t('tools.warnDisabled', { integration: integrationTitle })
      : null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <code className="font-mono text-sm font-medium">{tool.name}</code>
              <Badge variant={isStatic ? 'secondary' : 'success'} className="text-[10px]">
                {tool.source}
              </Badge>
              {!tool.enabled ? (
                <Badge variant="warning" className="text-[10px]">
                  disabled
                </Badge>
              ) : null}
              {tool.mutates ? (
                <Badge variant="warning" className="text-[10px]">
                  mutates
                </Badge>
              ) : null}
              {tool.kind ? (
                <Badge variant="outline" className="text-[10px]">
                  {tool.kind}
                </Badge>
              ) : null}
              {tool.seedVersion != null ? (
                <Badge variant="outline" className="text-[10px]">
                  seed v{tool.seedVersion}
                </Badge>
              ) : null}
            </div>
            <p className="text-sm text-muted-foreground">{tool.description}</p>
            {orphanWarning ? (
              <p className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-500">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {orphanWarning}
              </p>
            ) : null}
            {tool.target ? (
              <p className="text-[11px] font-mono text-muted-foreground">
                {'index' in tool.target
                  ? `index: ${tool.target.index}`
                  : 'table' in tool.target
                    ? `table: ${tool.target.table}`
                    : 'path' in tool.target
                      ? `${tool.target.method} ${tool.target.path}`
                      : ''}
              </p>
            ) : null}
            {tool.updatedAt ? (
              <p className="text-[10px] text-muted-foreground">
                {t('tools.updated', { date: formatDate(tool.updatedAt) })}
                {tool.updatedBy ? t('tools.updatedBy', { user: tool.updatedBy }) : ''}
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Switch
              checked={tool.enabled}
              disabled={toggling || cannotEnable}
              onCheckedChange={(enabled) => onToggle(enabled)}
              aria-label={
                cannotEnable
                  ? t('tools.cannotEnable', { integration: integrationTitle })
                  : tool.enabled
                    ? t('tools.disableTool')
                    : t('tools.enableTool')
              }
              title={
                cannotEnable
                  ? t('tools.cannotEnable', { integration: integrationTitle })
                  : tool.enabled
                    ? t('tools.disableTool')
                    : t('tools.enableTool')
              }
              className="mr-1"
            />
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:underline"
            >
              {expanded ? t('common.hide') : t('common.details')}
              <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onEdit}
              disabled={isStatic}
              title={isStatic ? t('tools.systemReadOnly') : t('tools.edit')}
            >
              <Pencil className="h-3 w-3" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              disabled={isStatic || busy}
              title={isStatic ? t('tools.systemReadOnly') : t('tools.delete')}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
        {cannotEnable ? (
          <p className="mt-2 text-[11px] text-muted-foreground">
            {t('tools.cannotEnable', { integration: integrationTitle })}
          </p>
        ) : null}
        {expanded ? (
          <div className="mt-3 space-y-2">
            <details className="rounded-md border bg-muted/30 p-3 text-[11px]">
              <summary className="cursor-pointer font-semibold">{t('tools.inputSchema')}</summary>
              <pre className="mt-2 max-h-96 overflow-auto">
                {JSON.stringify(tool.inputJsonSchema, null, 2)}
              </pre>
            </details>
            {tool.template ? (
              <details className="rounded-md border bg-muted/30 p-3 text-[11px]">
                <summary className="cursor-pointer font-semibold">{t('tools.template')}</summary>
                <pre className="mt-2 max-h-96 overflow-auto">
                  {typeof tool.template === 'string'
                    ? tool.template
                    : JSON.stringify(tool.template, null, 2)}
                </pre>
              </details>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
